import {useCallback} from 'react';
import {useQuotesFirst} from '@/matic/features/quoting/hooks/useQuotingAvailability';
import {create} from 'zustand';
import {useAppContext} from '@/matic/providers/AppProvider';
import {canUseRelayView} from '@/matic/config/features';

export type UiMode = 'classic' | 'otto';

const STORAGE_PREFIX = 'matic.uiMode.';

function safeStorage() {
  try {
    if (globalThis.window === undefined) return undefined;
    return globalThis.localStorage;
  } catch {
    return undefined;
  }
}

/**
 * Returns the user's explicitly stored mode, or `undefined` if they've never
 * toggled it. `undefined` lets the caller apply a mode-eligibility-aware
 * default instead of silently defaulting to `'classic'`.
 */
function readStoredMode(userId: string): UiMode | undefined {
  const storage = safeStorage();
  if (!storage) return undefined;
  try {
    const value = storage.getItem(STORAGE_PREFIX + userId);
    return value === 'otto' || value === 'classic' ? value : undefined;
  } catch {
    return undefined;
  }
}

function writeStoredMode(userId: string, mode: UiMode) {
  const storage = safeStorage();
  if (!storage) return;
  try {
    storage.setItem(STORAGE_PREFIX + userId, mode);
  } catch {
    // ignore quota / disabled-storage errors
  }
}

type UiModeStore = {
  modes: Record<string, UiMode>;
  setMode: (userId: string, mode: UiMode) => void;
};

const useUiModeStore = create<UiModeStore>((set) => ({
  modes: {},
  setMode(userId, mode) {
    set((state) => ({modes: {...state.modes, [userId]: mode}}));
  },
}));

/**
 * Per-user UI mode toggle. `mode` is always `'classic'` for tenants without
 * Relay-view access regardless of localStorage — the hook short-circuits
 * gating so a stale localStorage entry can't accidentally flip a tenant that
 * shouldn't see the Relay view. For Relay-eligible users who haven't
 * explicitly chosen a mode yet, the default is `'otto'` (Relay), not
 * `'classic'` (QMS) — the sidebar should open on Relay by default.
 */
export function useUiMode(): {mode: UiMode; setMode: (m: UiMode) => void} {
  const {userId, tenantId} = useAppContext();
  const storeMode = useUiModeStore((state) =>
    userId ? state.modes[userId] : undefined
  );
  const setStoreMode = useUiModeStore((state) => state.setMode);

  const relayViewEnabled = Boolean(tenantId && canUseRelayView(tenantId));

  const quotesFirst = useQuotesFirst() === true;

  let mode: UiMode = 'classic';
  if (quotesFirst) {
    // An AES tenant's shell has no QMS view to switch to.
    mode = 'otto';
  } else if (relayViewEnabled && userId) {
    mode = storeMode ?? readStoredMode(userId) ?? 'otto';
  }

  const setMode = useCallback(
    (next: UiMode) => {
      if (!relayViewEnabled || !userId) return;
      writeStoredMode(userId, next);
      setStoreMode(userId, next);
    },
    [relayViewEnabled, userId, setStoreMode]
  );

  return {mode, setMode};
}
